import {
  parseInfluencerInvoices,
  parseMohitInvoices,
  getActualInfluencerPaidAmount,
  getNetPayableBreakdown,
  getPayableOverrides,
  type PayableCampaignInput,
} from './paymentCalculations';

/**
 * Where each payable (fatura a pagar ao criador) stands:
 * - paid     — marked Paga.
 * - ready    — the client money that funds it has come in: pay it now.
 * - waiting  — the client hasn't paid that part yet.
 *
 * "Ready" is derived, never typed. A payable linked to specific client
 * invoices is ready when those are paid. Otherwise payables are funded in
 * order: payable N is ready once the creator's funded share covers
 * payables 1..N.
 */
export type PayableReadiness = 'paid' | 'ready' | 'waiting';

export interface PayableLike {
  id?: string;
  amount?: number;
  status?: string;
  funded_by_ids?: string[];
  source_name?: string;
}

interface ClientInvoiceLike {
  id?: string;
  status?: string;
}

// Tranche splits and client instalments round independently (12 916,63 vs
// 12 916,67); half a euro absorbs that without making a tranche ready early.
const ROUNDING_TOLERANCE = 0.5;

/**
 * @param fundedFor how much of the creator's money the client has funded
 *   so far (already paid out included), per source_name. Pass `pooled`
 *   when one figure covers every payable (single-creator campaigns).
 */
export function computePayableReadiness(
  payables: PayableLike[],
  clientInvoices: ClientInvoiceLike[],
  fundedFor: (sourceName: string) => number,
  { pooled = false }: { pooled?: boolean } = {},
): PayableReadiness[] {
  const clientById = new Map(
    clientInvoices.filter((c) => c.id).map((c) => [c.id as string, c]),
  );
  const running = new Map<string, number>();

  return payables.map((p) => {
    const key = pooled ? '' : p.source_name || '';
    const cumulative = (running.get(key) || 0) + (Number(p.amount) || 0);
    running.set(key, cumulative);

    if (p.status === 'Paid') return 'paid';

    const linked = (p.funded_by_ids || [])
      .map((id) => clientById.get(id))
      .filter((c): c is ClientInvoiceLike => !!c);
    if (linked.length > 0) {
      return linked.every((c) => c.status === 'Paid') ? 'ready' : 'waiting';
    }

    return cumulative <= fundedFor(key) + ROUNDING_TOLERANCE ? 'ready' : 'waiting';
  });
}

/**
 * Readiness of a single-creator campaign's payables, from the same money
 * rule as Pagamentos (paid out + available now = funded so far). Tranches
 * are before deductions, so they compare with the funded share before any
 * deduction is withheld.
 */
export function getPayableReadinessForCampaign(campaign: PayableCampaignInput): PayableReadiness[] {
  const payables = parseInfluencerInvoices(campaign);
  if (payables.length === 0) return [];
  const funded = getActualInfluencerPaidAmount(campaign)
    + getNetPayableBreakdown(campaign, getPayableOverrides(campaign)).grossPayable;
  return computePayableReadiness(payables, parseMohitInvoices(campaign), () => funded, { pooled: true });
}

/** "8–9", "3, 5–6" — 1-based positions of the payables ready to pay. */
export function formatReadyPositions(readiness: PayableReadiness[]): string {
  const positions = readiness
    .map((r, i) => (r === 'ready' ? i + 1 : null))
    .filter((n): n is number => n !== null);
  const parts: string[] = [];
  let start: number | null = null;
  let prev: number | null = null;
  for (const n of [...positions, Infinity]) {
    if (start === null) { start = n; prev = n; continue; }
    if (n === (prev as number) + 1) { prev = n; continue; }
    parts.push(start === prev ? String(start) : `${start}–${prev}`);
    start = n; prev = n;
  }
  return parts.filter((p) => p !== 'Infinity').join(', ');
}

/** "8–9 de 12": the creator's payables ready to pay now, or null when none are. */
export function describeReadyParcels(readiness: PayableReadiness[]): string | null {
  if (readiness.length === 0) return null;
  const ready = formatReadyPositions(readiness);
  return ready ? `${ready} de ${readiness.length}` : null;
}

/**
 * The "Parcelas prontas" cell in Pagamentos. Falls back to the old
 * client-instalment count only when the campaign has no payables at all.
 */
export function getParcelLabel(
  campaign: PayableCampaignInput,
  fallback: { paidInvoices: number; totalInvoices: number },
): string {
  return describeReadyParcels(getPayableReadinessForCampaign(campaign))
    ?? `${fallback.paidInvoices}/${fallback.totalInvoices}`;
}
